import { DefaultFooterComponent, Dropdown, Popup } from '@silverfort/sf-components';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Resource } from '../../../types/Resource';
import { DEFAULT_STALE_SETTING, StaleSettingsOptions } from '../../../common/staleStatus';

interface StaleSettingsModalContentComponentProps {
    popupTitle: string;
    onSelectState: (state?: number | Array<number>) => void;
    selectedState?: number;
}

const ContentComponent: FC<StaleSettingsModalContentComponentProps> = ({
    popupTitle,
    onSelectState,
    selectedState
}) => {
    return (
        <div className="text-13 py-4 min-h-[132px]">
            <div className="flex flex-col gap-4 px-6">
                <div className="leading-[19px]">{popupTitle}</div>

                <div className="flex flex-row justify-start gap-x-2 w-full h-full items-center">
                    <span>Users become stale after</span>

                    <Dropdown
                        items={StaleSettingsOptions}
                        onChange={(state) => onSelectState(state)}
                        selectedItems={selectedState}
                        variant="trigger"
                    />
                </div>
            </div>
        </div>
    );
};

interface StaleSettingsModalProps {
    currentState?: number;
    onApprove: (state: { resource_id?: string; stale_access_since: number }) => void;
    onClose: () => void;
    open: boolean;
    resource?: Resource | null;
}

export const StaleSettingsModal: FC<StaleSettingsModalProps> = ({
    currentState,
    onApprove,
    onClose,
    open,
    resource
}) => {
    const [staleState, setStaleState] = useState(currentState ?? DEFAULT_STALE_SETTING);

    useEffect(() => {
        setStaleState(currentState);
    }, [currentState]);

    const explanationText = useMemo(() => {
        return resource?.name
            ? `Specify the timeframe after which a user accessing "${resource.name}" will be considered stale.`
            : 'Specify the default timeframe after which a user accessing an application or associated resources will be considered stale.';
    }, [resource]);

    const handleApprove = useCallback(
        (state?: number) => {
            if (state) onApprove({ resource_id: resource?.resource_id, stale_access_since: state });

            onClose();
        },
        [onApprove, onClose, resource]
    );

    const onSelectState = useCallback((state?: number | Array<number>) => {
        const value = state && Array.isArray(state) ? state[0] : state;

        setStaleState(value);
    }, []);

    return (
        <Popup
            ContentComponent={ContentComponent}
            ContentComponentProps={{
                popupTitle: explanationText,
                onSelectState,
                selectedState: staleState
            }}
            FooterComponent={DefaultFooterComponent}
            onApprove={() => handleApprove(staleState)}
            onClose={onClose}
            open={open}
            title="Stale access setting"
        />
    );
};
